import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Stack,
  Collapse,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Popover,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import SickIcon from "@mui/icons-material/Sick";
import FlightIcon from "@mui/icons-material/Flight";
import CelebrationIcon from "@mui/icons-material/Celebration";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SectionCard from "./ui/SectionCard";
import axiosInstance from "./axiosInstance";
import dayjs from "dayjs";
import "dayjs/locale/de";
import isoWeek from "dayjs/plugin/isoWeek";
import isBetween from "dayjs/plugin/isBetween";

import { useBreadcrumbs } from './BreadcrumbBar';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.locale("de");

// ─── Constants ───────────────────────────────────────────────────────────────────

const ENTRY_TYPES = [
  { key: "vacation",      label: "Urlaub",      color: "#1D9E75", bgColor: "#E1F5EE", textColor: "#085041", dark: "#58C9A2", icon: BeachAccessIcon },
  { key: "sick",          label: "Krankheit",   color: "#D85A30", bgColor: "#FAECE7", textColor: "#4A1B0C", dark: "#F09070", icon: SickIcon },
  { key: "business_trip", label: "Dienstreise", color: "#185FA5", bgColor: "#E6F1FB", textColor: "#042C53", dark: "#6FAEE8", icon: FlightIcon },
  { key: "holiday",       label: "Feiertag",    color: "#BA7517", bgColor: "#FAEEDA", textColor: "#412402", dark: "#E3A24E", icon: CelebrationIcon },
  { key: "other",         label: "Sonstiges",   color: "#888780", bgColor: "#F1EFE8", textColor: "#2C2C2A", dark: "#A8A79E", icon: MoreHorizIcon },
];

// Typfarben je Farbschema: hell = heutige Pastellwerte, dunkel = heller
// Akzent als Text-/Iconfarbe auf transparenter Fläche.
const resolveTypeColors = (conf, theme) =>
  theme?.palette.mode === "dark"
    ? { ...conf, color: conf.dark, bgColor: alpha(conf.dark, 0.16), textColor: conf.dark }
    : conf;

const STATUS_CONFIG = {
  pending:   { label: "Angefragt",  color: "warning" },
  approved:  { label: "Genehmigt", color: "success" },
  rejected:  { label: "Abgelehnt", color: "error" },
  cancelled: { label: "Storniert", color: "default" },
};

const MEMBER_COLORS = [
  { color: "#534AB7", bg: "#EEEDFE", text: "#26215C", dark: "#9C93F2" },
  { color: "#1D9E75", bg: "#E1F5EE", text: "#04342C", dark: "#58C9A2" },
  { color: "#D85A30", bg: "#FAECE7", text: "#4A1B0C", dark: "#F09070" },
  { color: "#185FA5", bg: "#E6F1FB", text: "#042C53", dark: "#6FAEE8" },
  { color: "#BA7517", bg: "#FAEEDA", text: "#412402", dark: "#E3A24E" },
  { color: "#D4537E", bg: "#FBEAF0", text: "#4B1528", dark: "#EE8FAD" },
  { color: "#639922", bg: "#EAF3DE", text: "#173404", dark: "#9CCB5D" },
  { color: "#888780", bg: "#F1EFE8", text: "#2C2C2A", dark: "#A8A79E" },
];

// Rasterfarben je Farbschema — Light-Werte exakt wie bisher.
const gridColors = (theme) =>
  theme.palette.mode === "dark"
    ? {
        WEEKEND_BG: theme.palette.tokens.surface2,
        WEEKEND_TEXT: theme.palette.tokens.muted,
        DRAG_SELECTION_BG: alpha("#6FAEE8", 0.18),
        DRAG_SELECTION_BORDER: "#6FAEE8",
      }
    : {
        WEEKEND_BG: "#e4e4e4ff",
        WEEKEND_TEXT: "#928D7E",
        DRAG_SELECTION_BG: "#E6F1FB",
        DRAG_SELECTION_BORDER: "#185FA5",
      };

const getTypeConfig = (key, theme) =>
  resolveTypeColors(ENTRY_TYPES.find((t) => t.key === key) || ENTRY_TYPES[0], theme);
const getMemberColor = (index, theme) => {
  const mc = MEMBER_COLORS[index % MEMBER_COLORS.length];
  return theme?.palette.mode === "dark"
    ? { color: mc.dark, bg: alpha(mc.dark, 0.16), text: mc.dark }
    : mc;
};
const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const EMPTY_HOLIDAYS = new Set();

// Zählt Urlaubstage wie der Server: Mo–Fr, gesetzliche Feiertage (NRW,
// via /team-calendar/holidays/) kosten keinen Urlaubstag.
const countVacationDays = (start, end, halfDay, holidays = EMPTY_HOLIDAYS) => {
  if (!start || !end) return 0;
  const isFree = (d) => {
    const dow = d.day();
    return dow === 0 || dow === 6 || holidays.has(d.format("YYYY-MM-DD"));
  };
  if (halfDay) {
    return isFree(dayjs(start)) ? 0 : 0.5;
  }
  let d = dayjs(start);
  const endD = dayjs(end);
  let count = 0;
  while (d.isBefore(endD) || d.isSame(endD, "day")) {
    if (!isFree(d)) count += 1;
    d = d.add(1, "day");
  }
  return count;
};

const formatDays = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "–";
  return Number.isInteger(n) ? `${n}` : n.toFixed(1);
};

const normalizeRange = (a, b) => {
  if (!a || !b) return [a, b];
  return a <= b ? [a, b] : [b, a];
};

// ─── Legend Popover ──────────────────────────────────────────────────────────────

const LegendPopover = () => {
  const theme = useTheme();
  const { WEEKEND_BG } = gridColors(theme);
  const vacationConf = getTypeConfig("vacation", theme);
  const [anchorEl, setAnchorEl] = useState(null);
  const leaveTimer = useRef(null);

  const handleMouseEnter = (e) => {
    clearTimeout(leaveTimer.current);
    setAnchorEl(e.currentTarget);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setAnchorEl(null), 120);
  };

  return (
    <>
      <Tooltip title="Legende" placement="right">
        <IconButton
          size="small"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ color: "text.disabled", "&:hover": { color: "text.secondary" } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        disableScrollLock
        PaperProps={{
          onMouseEnter: () => clearTimeout(leaveTimer.current),
          onMouseLeave: handleMouseLeave,
          elevation: 3,
          sx: { p: 1.5, minWidth: 180, pointerEvents: "auto" },
        }}
        sx={{ pointerEvents: "none" }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.62rem", display: "block", mb: 1 }}>
          Eintragstypen
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {ENTRY_TYPES.map((t) => {
            const c = resolveTypeColors(t, theme);
            const Icon = t.icon;
            return (
              <Box key={t.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "4px", bgcolor: c.bgColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon sx={{ fontSize: "0.85rem", color: c.color }} />
                </Box>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "text.primary" }}>{t.label}</Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.62rem", display: "block", mb: 0.75 }}>
            Status
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 14, borderRadius: "2px", border: "1px dashed #aaa", opacity: 0.6 }} />
              <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Angefragt</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 14, borderRadius: "2px", bgcolor: vacationConf.bgColor, border: `1px solid ${vacationConf.color}` }} />
              <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Genehmigt</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 14, borderRadius: "2px", bgcolor: "#eee", opacity: 0.35 }} />
              <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Abgelehnt / Storniert</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 22, height: 14, borderRadius: "2px", bgcolor: WEEKEND_BG }} />
              <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary" }}>Wochenende / Feiertag (kostet keinen Urlaubstag)</Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// ─── Entry Dialog ─────────────────────────────────────────────────────────────────

const EntryDialog = ({
  open, entry, prefillDate, prefillEndDate,
  members, vacationBalances, currentUserId, isManager, holidays,
  onClose, onSave, onDelete, onApprove, onReject,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isEdit = Boolean(entry);
  // Read-only wenn fremder Eintrag.
  // Solange currentUserId noch nicht geladen ist (null), keinen Eintrag sperren.
  const isOwnEntry = !isEdit || !currentUserId || entry.employee === currentUserId;
  const readOnly = isEdit && !isOwnEntry;

  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useBreadcrumbs([{ label: 'Teamkalender' }]);

  useEffect(() => {
    if (open) {
      setForm(
        entry
          ? {
              employee:   entry.employee ?? "",
              type:       entry.type ?? "vacation",
              status:     entry.status ?? "pending",
              start_date: entry.start_date ?? "",
              end_date:   entry.end_date ?? "",
              half_day:   entry.half_day ?? false,
              notes:      entry.notes ?? "",
            }
          : {
              // Neuer Eintrag: immer auf den aktuellen User vorbelegen
              employee:   currentUserId ?? members[0]?.id ?? "",
              type:       "vacation",
              status:     "pending",
              start_date: prefillDate ?? dayjs().format("YYYY-MM-DD"),
              end_date:   prefillEndDate ?? prefillDate ?? dayjs().format("YYYY-MM-DD"),
              half_day:   false,
              notes:      "",
            }
      );
      setErrors({});
    }
  }, [open, entry, prefillDate, prefillEndDate, members, currentUserId]);

  const set = (field) => (e) => {
    if (readOnly) return;
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleTypeChange = (e) => {
    if (readOnly) return;
    const newType = e.target.value;
    setForm((p) => ({ ...p, type: newType, half_day: newType === "vacation" ? p.half_day : false }));
  };

  const handleStartDateChange = (e) => {
    if (readOnly) return;
    const value = e.target.value;
    setForm((p) => ({ ...p, start_date: value, end_date: p.half_day ? value : p.end_date }));
    setErrors((p) => ({ ...p, start_date: "" }));
  };

  const handleHalfDayToggle = (e) => {
    if (readOnly) return;
    const checked = e.target.checked;
    setForm((p) => ({ ...p, half_day: checked, end_date: checked ? p.start_date : p.end_date }));
  };

  const validate = () => {
    const e = {};
    if (!form.employee) e.employee = "Pflichtfeld";
    if (!form.start_date) e.start_date = "Pflichtfeld";
    if (!form.end_date) e.end_date = "Pflichtfeld";
    if (form.start_date && form.end_date && form.end_date < form.start_date)
      e.end_date = "Enddatum muss nach Startdatum liegen";
    if (form.half_day && form.start_date !== form.end_date)
      e.end_date = "Bei einem halben Tag müssen Start- und Enddatum gleich sein";
    return e;
  };

  const handleSave = async () => {
    if (readOnly) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try { await onSave(isEdit ? entry.id : null, form); onClose(); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    setLoading(true);
    try { await onDelete(entry.id); onClose(); }
    finally { setLoading(false); }
  };

  const handleApprove = async () => {
    setLoading(true);
    try { await onApprove(entry.id); onClose(); }
    finally { setLoading(false); }
  };

  const handleReject = async () => {
    setLoading(true);
    try { await onReject(entry.id); onClose(); }
    finally { setLoading(false); }
  };

  const statusConf = STATUS_CONFIG[form.status] || STATUS_CONFIG.pending;
  const balance = form.employee ? vacationBalances?.[form.employee] : null;
  const requestedDays = form.type === "vacation" ? countVacationDays(form.start_date, form.end_date, form.half_day, holidays) : 0;
  const originalDays = isEdit && entry?.type === "vacation"
    ? countVacationDays(entry.start_date, entry.end_date, entry.half_day, holidays) : 0;
  // K1-Regeln fuers Feedback im Dialog: < 2 Tage genehmigt der Server sofort.
  const willAutoApprove = form.type !== "vacation" || requestedDays < 2;
  const effectiveRemaining = balance ? balance.remaining + originalDays : null;
  const exceedsBalance = form.type === "vacation" && effectiveRemaining !== null && requestedDays > effectiveRemaining;

  // Name des Eintraginhabers für den Read-Only-Titel
  const ownerMember = isEdit ? members.find((m) => m.id === entry.employee) : null;
  const ownerName = ownerMember
    ? (ownerMember.first_name ? `${ownerMember.first_name} ${ownerMember.last_name}` : ownerMember.username)
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {readOnly && <LockOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />}
          {readOnly ? `Eintrag von ${ownerName}` : isEdit ? "Eintrag bearbeiten" : "Neuer Eintrag"}
        </Box>
        {isEdit && (
          <Chip label={statusConf.label} color={statusConf.color} size="small" variant="outlined" />
        )}
      </DialogTitle>

      {/* Read-only Hinweisband */}
      {readOnly && (
        <Box sx={{ mx: 3, mb: 0.5, px: 1.5, py: 0.75, bgcolor: "action.hover", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon sx={{ fontSize: "0.85rem", color: "text.disabled" }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Du kannst nur deine eigenen Einträge bearbeiten.
          </Typography>
        </Box>
      )}

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

          {/* Mitarbeiter – nur bei fremden Einträgen (read-only) anzeigen */}
          {readOnly && (() => {
            const ownerM = members.find((m) => m.id === form.employee);
            const mc = getMemberColor(ownerM ? ownerM._index : 0, theme);
            const name = ownerM
              ? (ownerM.first_name ? `${ownerM.first_name} ${ownerM.last_name}` : ownerM.username)
              : "–";
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: mc.color }}>
                  {initials(name)}
                </Avatar>
                <Box>
                  <Typography variant="caption" sx={{ color: "text.disabled", display: "block", lineHeight: 1.2 }}>Mitarbeiter</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Typ */}
          <TextField
            select
            label="Typ"
            value={form.type ?? "vacation"}
            onChange={handleTypeChange}
            fullWidth
            disabled={readOnly}
          >
            {ENTRY_TYPES.map((t) => {
              const c = resolveTypeColors(t, theme);
              const Icon = t.icon;
              return (
                <MenuItem key={t.key} value={t.key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: "1rem", color: c.color }} />
                    {t.label}
                  </Box>
                </MenuItem>
              );
            })}
          </TextField>

          {/* Status: nur Manager sehen und ändern ihn — der Server erzwingt
              das ohnehin (S4); Nicht-Manager sehen den Status im Kopf-Chip. */}
          {isEdit && isManager && (
            <TextField
              select
              label="Status"
              value={form.status ?? "pending"}
              onChange={set("status")}
              fullWidth
              disabled={readOnly}
            >
              {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
                <MenuItem key={key} value={key}>
                  <Chip label={conf.label} color={conf.color} size="small" variant="outlined" sx={{ pointerEvents: "none" }} />
                </MenuItem>
              ))}
            </TextField>
          )}

          {isEdit && entry.status === "approved" && (
            <Typography variant="body2" color="text.secondary">
              {entry.approved_by
                ? `Genehmigt am ${new Date(entry.approved_at).toLocaleDateString("de-DE")} durch ${
                    members.find((m) => m.id === entry.approved_by)?.first_name
                      ? `${members.find((m) => m.id === entry.approved_by).first_name} ${members.find((m) => m.id === entry.approved_by).last_name}`
                      : members.find((m) => m.id === entry.approved_by)?.username ?? "—"}`
                : `Automatisch genehmigt${entry.approved_at ? ` am ${new Date(entry.approved_at).toLocaleDateString("de-DE")}` : ""}`}
            </Typography>
          )}

          {isEdit && entry.status === "rejected" && (
            <Box>
              Abgelehnt am{" "}
              {new Date(entry.approved_at).toLocaleDateString("de-DE")}{" "}
              durch {members.find(m => m.id === entry.approved_by)?.first_name
                ? `${members.find(m => m.id === entry.approved_by).first_name} ${members.find(m => m.id === entry.approved_by).last_name}`
                : members.find(m => m.id === entry.approved_by)?.username}
            </Box>
          )}

          {/* Halber Tag */}
          {form.type === "vacation" && (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={<Checkbox checked={!!form.half_day} onChange={handleHalfDayToggle} size="small" disabled={readOnly} />}
              label="Halber Tag (0,5 Urlaubstage)"
            />
          )}

          {/* Datum */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Von"
              type="date"
              value={form.start_date ?? ""}
              onChange={handleStartDateChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ readOnly }}
              error={!!errors.start_date}
              helperText={errors.start_date}
            />
            <TextField
              label="Bis"
              type="date"
              value={form.half_day ? (form.start_date ?? "") : (form.end_date ?? "")}
              onChange={(e) => { set("end_date")(e); if (!form.start_date) set("start_date")(e); }}
              fullWidth
              disabled={!!form.half_day || readOnly}
              InputLabelProps={{ shrink: true }}
              error={!!errors.end_date}
              helperText={errors.end_date}
            />
          </Box>

          {/* Urlaubskonto-Info */}
          {form.type === "vacation" && form.employee && (
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 1, px: 1.5, py: 1, borderRadius: 1,
              bgcolor: exceedsBalance ? (isDark ? alpha(theme.palette.error.main, 0.14) : "#FAECE7") : "action.hover",
            }}>
              <Typography variant="body2">
                Beantragt: <strong>{formatDays(requestedDays)}</strong> {requestedDays === 1 ? "Tag" : "Tage"}
              </Typography>
              <Typography variant="body2" sx={{ color: exceedsBalance ? (isDark ? theme.palette.error.main : "#4A1B0C") : "text.secondary", fontWeight: exceedsBalance ? 600 : 400 }}>
                {balance
                  ? `Resturlaub: ${formatDays(effectiveRemaining)} von ${formatDays(balance.total)} Tagen${exceedsBalance ? " · überschreitet Resturlaub" : ""}`
                  : "Resturlaub: keine Daten"}
              </Typography>
            </Box>
          )}

          {/* Hinweis auf die Genehmigungsregel (Server erzwingt sie) */}
          {form.type === "vacation" && !readOnly && (
            <Typography variant="caption" sx={{ color: willAutoApprove ? "success.main" : "warning.main", fontWeight: 600, mt: -1 }}>
              {willAutoApprove
                ? "Unter 2 Tagen — wird automatisch genehmigt."
                : "Ab 2 Tagen — benötigt die Freigabe eines Managers."}
            </Typography>
          )}

          {/* Notizen */}
          <TextField
            label="Notizen (optional)"
            value={form.notes ?? ""}
            onChange={set("notes")}
            fullWidth
            multiline
            rows={3}
            inputProps={{ readOnly }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Löschen nur für eigene Einträge */}
          {isEdit && !readOnly && (
            <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={loading}>
              Löschen
            </Button>
          )}
          {/* Genehmigen/Ablehnen nur für Manager (Security S4) */}
          {isEdit && isManager && entry.status === "pending" && (
            <>
              <Button onClick={handleApprove} color="success" variant="outlined" size="small"
                startIcon={<CheckCircleOutlineIcon />} disabled={loading}>
                Genehmigen
              </Button>
              <Button onClick={handleReject} color="error" variant="outlined" size="small"
                startIcon={<CancelOutlinedIcon />} disabled={loading}>
                Ablehnen
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={loading}>{readOnly ? "Schließen" : "Abbrechen"}</Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="contained" disabled={loading}>
              {loading ? "Speichern…" : "Speichern"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─── Entry Pill ───────────────────────────────────────────────────────────────────

const EntryPill = ({ entry, members, onClick, isStart, isEnd, isSingle }) => {
  const theme = useTheme();
  const typeConf = getTypeConfig(entry.type, theme);
  const TypeIcon = typeConf.icon;
  const member = members.find((m) => m.id === entry.employee);
  const mc = getMemberColor(member ? member._index : 0, theme);
  const isPending  = entry.status === "pending";
  const isRejected = entry.status === "rejected" || entry.status === "cancelled";
  const borderRadius = isSingle ? "4px" : isStart ? "4px 0 0 4px" : isEnd ? "0 4px 4px 0" : "0";
  const memberName = member ? (member.first_name ? member.first_name : member.username) : "—";
  const halfDaySuffix = entry.half_day ? " (½ Tag)" : "";

  return (
    <Tooltip title={`${memberName} · ${typeConf.label}${halfDaySuffix} · ${STATUS_CONFIG[entry.status]?.label ?? entry.status}`} placement="top">
      <Box
        onMouseDown={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); onClick(entry); }}
        sx={{
          px: 0.5, py: 0.25,
          bgcolor: typeConf.bgColor,
          borderLeft: isStart || isSingle ? `3px solid ${mc.color}` : "none",
          borderRadius,
          cursor: "pointer",
          overflow: "hidden",
          opacity: isRejected ? 0.35 : isPending ? 0.7 : 1,
          outline: isPending ? `1px dashed ${typeConf.color}` : "none",
          "&:hover": { filter: "brightness(0.95)" },
          display: "flex", alignItems: "center", gap: 0.5, minWidth: 0,
        }}
      >
        <TypeIcon sx={{ fontSize: "0.9rem", color: typeConf.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: typeConf.textColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {memberName} · {typeConf.label}{halfDaySuffix}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ─── Month View ───────────────────────────────────────────────────────────────────

const MonthView = ({ currentDate, entries, members, holidays = EMPTY_HOLIDAYS, onDayClick, onEntryClick, onDragStart, onDragEnter, isDaySelected }) => {
  const theme = useTheme();
  const { WEEKEND_BG, WEEKEND_TEXT, DRAG_SELECTION_BG, DRAG_SELECTION_BORDER } = gridColors(theme);
  const startOfGrid = currentDate.startOf("month").startOf("isoWeek");
  const endOfGrid   = currentDate.endOf("month").endOf("isoWeek");
  const today       = dayjs();
  const days = [];
  let d = startOfGrid;
  while (d.isBefore(endOfGrid) || d.isSame(endOfGrid, "day")) { days.push(d); d = d.add(1, "day"); }
  const weekdays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const getDayEntries = (date) => { const ds = date.format("YYYY-MM-DD"); return entries.filter((e) => ds >= e.start_date && ds <= e.end_date); };

  return (
    <Box sx={{ userSelect: "none" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
        {weekdays.map((wd, i) => (
          <Typography key={wd} variant="caption" sx={{ textAlign: "center", color: (i === 5 || i === 6) ? WEEKEND_TEXT : "text.secondary", fontWeight: 600, fontSize: "0.7rem" }}>{wd}</Typography>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {days.map((day) => {
          const dayEntries = getDayEntries(day);
          const isToday   = day.isSame(today, "day");
          const isCurrent = day.month() === currentDate.month();
          const ds        = day.format("YYYY-MM-DD");
          const isHoliday = holidays.has(ds);
          const isFree    = day.day() === 0 || day.day() === 6 || isHoliday;
          const isSelected = isDaySelected(ds);
          return (
            <Box key={ds} onMouseDown={() => onDragStart(day)} onMouseEnter={() => onDragEnter(day)} onClick={() => onDayClick(day)}
              sx={{ minHeight: 90, p: 0.75, cursor: "pointer", border: "1px solid", borderColor: isSelected ? DRAG_SELECTION_BORDER : isToday ? "primary.main" : "divider", borderRadius: 1.5, bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.50" : isFree ? WEEKEND_BG : isCurrent ? "background.paper" : "action.hover", opacity: isCurrent ? 1 : 0.4, "&:hover": { bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.100" : "action.selected" }, display: "flex", flexDirection: "column", gap: 0.25, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 400, color: isToday ? "primary.main" : isFree ? WEEKEND_TEXT : "text.primary", fontSize: "0.75rem", lineHeight: 1 }}>{day.date()}</Typography>
                {isHoliday && <Typography variant="caption" sx={{ fontSize: "0.58rem", color: WEEKEND_TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Feiertag</Typography>}
              </Box>
              {dayEntries.slice(0, 3).map((entry) => (
                <EntryPill key={entry.id} entry={entry} members={members} onClick={onEntryClick} isStart={ds === entry.start_date} isEnd={ds === entry.end_date} isSingle={entry.start_date === entry.end_date} />
              ))}
              {dayEntries.length > 3 && <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6rem" }}>+{dayEntries.length - 3} weitere</Typography>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── Week View ────────────────────────────────────────────────────────────────────

const WeekView = ({ currentDate, entries, members, holidays = EMPTY_HOLIDAYS, onDayClick, onEntryClick, onDragStart, onDragEnter, isDaySelected }) => {
  const theme = useTheme();
  const { WEEKEND_BG, WEEKEND_TEXT, DRAG_SELECTION_BG, DRAG_SELECTION_BORDER } = gridColors(theme);
  const startOfWeek = currentDate.startOf("isoWeek");
  const today       = dayjs();
  const days        = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));
  const weekdays    = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const getDayEntries = (date) => { const ds = date.format("YYYY-MM-DD"); return entries.filter((e) => ds >= e.start_date && ds <= e.end_date); };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.75, userSelect: "none" }}>
      {days.map((day, i) => {
        const dayEntries = getDayEntries(day);
        const isToday   = day.isSame(today, "day");
        const ds        = day.format("YYYY-MM-DD");
        const isFree    = day.day() === 0 || day.day() === 6 || holidays.has(ds);
        const isSelected = isDaySelected(ds);
        return (
          <Box key={ds}>
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: isToday ? "primary.main" : isFree ? WEEKEND_TEXT : "text.secondary", fontWeight: isToday ? 700 : 600, fontSize: "0.7rem", mb: 0.5 }}>{weekdays[i]}, {day.format("DD.MM.")}</Typography>
            <Box onMouseDown={() => onDragStart(day)} onMouseEnter={() => onDragEnter(day)} onClick={() => onDayClick(day)}
              sx={{ minHeight: 140, p: 0.75, cursor: "pointer", border: "1px solid", borderColor: isSelected ? DRAG_SELECTION_BORDER : isToday ? "primary.main" : "divider", borderRadius: 1.5, bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.50" : isFree ? WEEKEND_BG : "background.paper", "&:hover": { bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.100" : "action.selected" }, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {dayEntries.map((entry) => (
                <EntryPill key={entry.id} entry={entry} members={members} onClick={onEntryClick} isStart={ds === entry.start_date} isEnd={ds === entry.end_date} isSingle={entry.start_date === entry.end_date} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Work Week View ───────────────────────────────────────────────────────────────

const WorkWeekView = ({ currentDate, entries, members, holidays = EMPTY_HOLIDAYS, onDayClick, onEntryClick, onDragStart, onDragEnter, isDaySelected }) => {
  const theme = useTheme();
  const { WEEKEND_BG, WEEKEND_TEXT, DRAG_SELECTION_BG, DRAG_SELECTION_BORDER } = gridColors(theme);
  const startOfWeek = currentDate.startOf("isoWeek");
  const today       = dayjs();
  const days        = Array.from({ length: 5 }, (_, i) => startOfWeek.add(i, "day"));
  const weekdays    = ["Mo", "Di", "Mi", "Do", "Fr"];
  const getDayEntries = (date) => { const ds = date.format("YYYY-MM-DD"); return entries.filter((e) => ds >= e.start_date && ds <= e.end_date); };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 0.75, userSelect: "none" }}>
      {days.map((day, i) => {
        const dayEntries = getDayEntries(day);
        const isToday    = day.isSame(today, "day");
        const ds         = day.format("YYYY-MM-DD");
        const isHoliday  = holidays.has(ds);
        const isSelected = isDaySelected(ds);
        return (
          <Box key={ds}>
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: isToday ? "primary.main" : isHoliday ? WEEKEND_TEXT : "text.secondary", fontWeight: isToday ? 700 : 600, fontSize: "0.7rem", mb: 0.5 }}>{weekdays[i]}, {day.format("DD.MM.")}{isHoliday ? " · Feiertag" : ""}</Typography>
            <Box onMouseDown={() => onDragStart(day)} onMouseEnter={() => onDragEnter(day)} onClick={() => onDayClick(day)}
              sx={{ minHeight: 140, p: 0.75, cursor: "pointer", border: "1px solid", borderColor: isSelected ? DRAG_SELECTION_BORDER : isToday ? "primary.main" : "divider", borderRadius: 1.5, bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.50" : isHoliday ? WEEKEND_BG : "background.paper", "&:hover": { bgcolor: isSelected ? DRAG_SELECTION_BG : isToday ? "primary.100" : "action.selected" }, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {dayEntries.map((entry) => (
                <EntryPill key={entry.id} entry={entry} members={members} onClick={onEntryClick} isStart={ds === entry.start_date} isEnd={ds === entry.end_date} isSingle={entry.start_date === entry.end_date} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Year View ────────────────────────────────────────────────────────────────────

const YearView = ({ currentDate, entries, members, holidays = EMPTY_HOLIDAYS, onDayClick }) => {
  const theme = useTheme();
  const { WEEKEND_BG, WEEKEND_TEXT } = gridColors(theme);
  const year   = currentDate.year();
  const months = Array.from({ length: 12 }, (_, i) => dayjs(`${year}-${String(i + 1).padStart(2, "0")}-01`));
  const getDayEntries = (date) => { const ds = date.format("YYYY-MM-DD"); return entries.filter((e) => ds >= e.start_date && ds <= e.end_date); };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
      {months.map((month) => {
        const startOfMonth = month.startOf("month");
        const daysInMonth  = month.daysInMonth();
        const firstDow     = (startOfMonth.day() + 6) % 7;
        return (
          <Box key={month.format("YYYY-MM")}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.primary", display: "block", mb: 0.5 }}>{month.locale("de").format("MMMM")}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.25 }}>
              {["M","D","M","D","F","S","S"].map((wd, i) => (
                <Typography key={i} sx={{ fontSize: "0.55rem", textAlign: "center", color: (i === 5 || i === 6) ? WEEKEND_TEXT : "text.disabled" }}>{wd}</Typography>
              ))}
              {Array.from({ length: firstDow }).map((_, i) => <Box key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const date        = startOfMonth.add(i, "day");
                const dayEntries  = getDayEntries(date);
                const isToday     = date.isSame(dayjs(), "day");
                const isWeekend   = date.day() === 0 || date.day() === 6 || holidays.has(date.format("YYYY-MM-DD"));
                const primaryType = dayEntries[0] ? getTypeConfig(dayEntries[0].type, theme) : null;
                const PrimaryIcon = primaryType?.icon;
                const extraCount  = dayEntries.length > 1 ? dayEntries.length - 1 : 0;
                return (
                  <Tooltip key={i} placement="top"
                    title={dayEntries.length > 0 ? dayEntries.map((e) => { const m = members.find((m) => m.id === e.employee); const name = m ? (m.first_name || m.username) : "?"; return `${name}: ${getTypeConfig(e.type, theme).label}${e.half_day ? " (½ Tag)" : ""} (${STATUS_CONFIG[e.status]?.label ?? e.status})`; }).join(", ") : ""}>
                    <Box onClick={() => onDayClick(date)}
                      sx={{ width: "100%", aspectRatio: "1", borderRadius: "3px", cursor: "pointer", position: "relative", bgcolor: dayEntries.length === 0 ? (isToday ? "primary.light" : isWeekend ? WEEKEND_BG : "action.hover") : primaryType.bgColor, border: isToday ? "1px solid" : "1px solid transparent", borderColor: isToday ? "primary.main" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { transform: "scale(1.15)", filter: "brightness(0.95)" }, transition: "transform 0.1s" }}>
                      {PrimaryIcon && <PrimaryIcon sx={{ fontSize: "0.7rem", color: primaryType.color }} />}
                      {extraCount > 0 && <Box sx={{ position: "absolute", bottom: -2, right: -2, width: 8, height: 8, borderRadius: "50%", bgcolor: "background.paper", border: "1px solid", borderColor: "divider", fontSize: "0.5rem", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>+</Box>}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Urlaubsbilanz (eigene Karte) ─────────────────────────────────────────────────

const BalanceCard = ({ balance }) => {
  if (!balance) return null;
  const b = balance;
  const spent = (b.taken ?? 0) + (b.reserved ?? 0);
  const pct = b.total > 0 ? Math.min(100, (spent / b.total) * 100) : 0;
  const low = b.remaining <= 2;
  const deadlinePassed = b.carryover_deadline && dayjs().isAfter(dayjs(b.carryover_deadline));

  return (
    <SectionCard sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        Dein Urlaub {b.year}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: low ? "error.main" : "primary.main", fontVariantNumeric: "tabular-nums" }}>
          {formatDays(b.remaining)}
        </Typography>
        <Typography variant="body2" color="text.secondary">Tage verfügbar</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { borderRadius: 3 } }} />
      <Typography variant="caption" color="text.secondary">
        Anspruch {formatDays(b.annual_days)}
        {b.carryover_days > 0 && ` + ${formatDays(b.carryover_days - (b.carryover_forfeited || 0))} Übertrag`}
        {" · "}Genommen {formatDays(b.taken)}
        {b.reserved > 0 && ` · Angefragt ${formatDays(b.reserved)}`}
      </Typography>
      {b.carryover_days > 0 && !deadlinePassed && (
        <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
          Übertrag bis {dayjs(b.carryover_deadline).format("DD.MM.")} nutzen — danach verfällt er.
        </Typography>
      )}
      {(b.carryover_forfeited || 0) > 0 && (
        <Typography variant="caption" color="text.disabled">
          {formatDays(b.carryover_forfeited)} Übertragstag{b.carryover_forfeited === 1 ? "" : "e"} am {dayjs(b.carryover_deadline).format("DD.MM.")} verfallen.
        </Typography>
      )}
    </SectionCard>
  );
};

// ─── Genehmigungs-Postfach (nur Manager) ─────────────────────────────────────────

const ApprovalInbox = ({ entries, members, holidays, onApprove, onReject, onEntryClick }) => {
  const theme = useTheme();
  const pending = entries
    .filter((e) => e.status === "pending")
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  if (!pending.length) return null;

  const memberName = (id) => {
    const m = members.find((x) => x.id === id);
    return m ? (m.first_name ? `${m.first_name} ${m.last_name}` : m.username) : "—";
  };
  // Überschneidung mit anderen (nicht abgelehnten) Abwesenheiten
  const overlaps = (entry) => entries
    .filter((e) => e.id !== entry.id && e.employee !== entry.employee
      && e.status !== "rejected" && e.status !== "cancelled"
      && e.start_date <= entry.end_date && e.end_date >= entry.start_date)
    .map((e) => memberName(e.employee));

  return (
    <SectionCard>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4, display: "block", mb: 1 }}>
        Offene Anträge ({pending.length})
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {pending.map((entry, idx) => {
          const m = members.find((x) => x.id === entry.employee);
          const mc = getMemberColor(m ? m._index : 0, theme);
          const days = countVacationDays(entry.start_date, entry.end_date, entry.half_day, holidays);
          const conflictNames = [...new Set(overlaps(entry))];
          const typeConf = getTypeConfig(entry.type, theme);
          return (
            <Box key={entry.id} sx={{
              display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", py: 1.25,
              borderTop: idx === 0 ? "none" : "1px solid", borderColor: "divider",
            }}>
              <Avatar sx={{ width: 30, height: 30, fontSize: "0.72rem", fontWeight: 700, bgcolor: mc.color }}>
                {initials(memberName(entry.employee))}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1, cursor: "pointer" }} onClick={() => onEntryClick(entry)}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {memberName(entry.employee)} · {typeConf.label}
                  {entry.type === "vacation" && ` · ${formatDays(days)} ${days === 1 ? "Tag" : "Tage"}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {dayjs(entry.start_date).format("DD.MM.")}–{dayjs(entry.end_date).format("DD.MM.YYYY")}
                  {conflictNames.length > 0 && (
                    <Box component="span" sx={{ color: "warning.main", fontWeight: 600 }}>
                      {" "}· überschneidet sich mit {conflictNames.join(", ")}
                    </Box>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" color="success" variant="outlined" startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => onApprove(entry.id)}>
                  Genehmigen
                </Button>
                <Button size="small" color="error" variant="outlined" startIcon={<CancelOutlinedIcon />}
                  onClick={() => onReject(entry.id)}>
                  Ablehnen
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

// ─── Team-Urlaubskonten (nur Manager) ────────────────────────────────────────────

const EditableDays = ({ value, onCommit }) => {
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => { setLocal(value ?? ""); }, [value]);
  const commit = () => {
    const n = parseFloat(local);
    if (!Number.isNaN(n) && n >= 0 && n !== value) onCommit(n);
    else setLocal(value ?? "");
  };
  return (
    <TextField
      value={local} size="small" type="number"
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setLocal(value ?? ""); e.target.blur(); } }}
      inputProps={{ min: 0, step: 0.5, style: { padding: "4px 8px", width: 52, textAlign: "right", fontVariantNumeric: "tabular-nums" } }}
    />
  );
};

const TeamAccountsPanel = ({ members, balances, onUpdateAllowance }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const rows = members
    .map((m) => ({ member: m, bal: balances[m.id] }))
    .filter((r) => r.bal);
  if (!rows.length) return null;

  return (
    <SectionCard sx={{ p: 0 }}>
      <Box onClick={() => setOpen((o) => !o)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.75, cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, borderRadius: "14px" }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          Team-Urlaubskonten {dayjs().year()}
        </Typography>
        <ExpandMoreIcon sx={{ color: "text.disabled", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2, overflowX: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(160px, 1fr) 90px 90px 80px 80px 80px", gap: 1, alignItems: "center", minWidth: 620 }}>
            {["Mitarbeiter", "Anspruch", "Übertrag", "Genommen", "Angefragt", "Rest"].map((h) => (
              <Typography key={h} variant="caption" sx={{ color: "text.disabled", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.62rem", textAlign: h === "Mitarbeiter" ? "left" : "right" }}>{h}</Typography>
            ))}
            {rows.map(({ member, bal }) => {
              const mc = getMemberColor(member._index, theme);
              const name = member.first_name ? `${member.first_name} ${member.last_name}` : member.username;
              return (
                <React.Fragment key={member.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 26, height: 26, fontSize: "0.65rem", fontWeight: 700, bgcolor: mc.color }}>{initials(name)}</Avatar>
                    <Typography variant="body2" noWrap>{name}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <EditableDays value={bal.annual_days} onCommit={(v) => onUpdateAllowance(bal.allowance_id, { annual_days: v }, member.id)} />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <EditableDays value={bal.carryover_days} onCommit={(v) => onUpdateAllowance(bal.allowance_id, { carryover_days: v }, member.id)} />
                  </Box>
                  <Typography variant="body2" sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatDays(bal.taken)}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: bal.reserved > 0 ? "warning.main" : "text.disabled" }}>{formatDays(bal.reserved)}</Typography>
                  <Typography variant="body2" sx={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: bal.remaining <= 2 ? "error.main" : "text.primary" }}>{formatDays(bal.remaining)}</Typography>
                </React.Fragment>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
            Anspruch und Übertrag direkt bearbeiten — Enter oder Klick daneben speichert. Übertrag verfällt am 31.03.
          </Typography>
        </Box>
      </Collapse>
    </SectionCard>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────────

const TeamCalendar = () => {
  const [entries, setEntries]                   = useState([]);
  const [members, setMembers]                   = useState([]);
  const [currentDate, setCurrentDate]           = useState(dayjs());
  const [viewMode, setViewMode]                 = useState("month");
  const [filterMembers, setFilterMembers]       = useState([]);
  const [vacationBalances, setVacationBalances] = useState({});
  const [currentUserId, setCurrentUserId]       = useState(null);
  const [isManager, setIsManager]               = useState(false);
  const [holidays, setHolidays]                 = useState(EMPTY_HOLIDAYS);
  const holidayYearsRef                         = useRef(new Set());
  const theme = useTheme();

  const [dialogOpen, setDialogOpen]         = useState(false);
  const [selectedEntry, setSelectedEntry]   = useState(null);
  const [selectedDate, setSelectedDate]     = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [dragAnchor, setDragAnchor]   = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const isDraggingRef  = useRef(false);
  const mouseIsDownRef = useRef(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, usersRes, meRes] = await Promise.all([
        axiosInstance.get("/team-calendar/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        }),
        axiosInstance.get("/users/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        }),
        // Aktuell eingeloggten User ermitteln
        axiosInstance.get("/me/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        }).catch(() => null),
      ]);
      const membersWithIndex = usersRes.data.map((m, i) => ({ ...m, _index: i }));
      setMembers(membersWithIndex);
      setFilterMembers(membersWithIndex.map((m) => m.id));
      setEntries(entriesRes.data);
      if (meRes) {
        setCurrentUserId(meRes.data.id);
        setIsManager(!!meRes.data.is_staff);
      }
      fetchVacationBalances(membersWithIndex, meRes?.data);
    } catch (err) {
      console.error("Fehler beim Laden:", err);
    }
  };

  // Bilanzen rollenbewusst laden: Mitarbeiter duerfen nur die eigene
  // abfragen (S3) — Manager alle (fuer Postfach + Team-Konten).
  const fetchVacationBalances = async (membersList, me) => {
    try {
      const year = dayjs().year();
      const targets = me && !me.is_staff
        ? membersList.filter((m) => m.id === me.id)
        : membersList;
      const results = await Promise.all(
        targets.map((m) =>
          axiosInstance.get("/team-calendar/vacation_balance/", {
            params: { employee: m.id, year },
          }).then((res) => [m.id, res.data]).catch(() => [m.id, null])
        )
      );
      setVacationBalances(Object.fromEntries(results.filter(([, v]) => v)));
    } catch (err) {
      console.error("Fehler beim Laden der Urlaubskonten:", err);
    }
  };

  // Gesetzliche Feiertage (NRW) fuer das angezeigte Jahr — fuer die
  // Raster-Schattierung und die Tageszaehlung im Dialog.
  useEffect(() => {
    const year = currentDate.year();
    if (holidayYearsRef.current.has(year)) return;
    holidayYearsRef.current.add(year);
    axiosInstance.get("/team-calendar/holidays/", { params: { year } })
      .then((res) => {
        setHolidays((prev) => new Set([...prev, ...res.data.holidays]));
      })
      .catch(() => { holidayYearsRef.current.delete(year); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  // Urlaubskonto eines Mitarbeiters aendern (nur Manager) + Bilanz auffrischen
  const handleUpdateAllowance = async (allowanceId, patch, memberId) => {
    try {
      await axiosInstance.patch(`/vacation-allowances/${allowanceId}/`, patch);
      const res = await axiosInstance.get("/team-calendar/vacation_balance/", {
        params: { employee: memberId, year: dayjs().year() },
      });
      setVacationBalances((prev) => ({ ...prev, [memberId]: res.data }));
    } catch (err) {
      console.error("Fehler beim Aktualisieren des Urlaubskontos:", err);
    }
  };

  const filteredEntries = useMemo(
    () => entries.filter((e) => filterMembers.includes(e.employee)),
    [entries, filterMembers]
  );

  const handleDragStart = useCallback((day) => {
    mouseIsDownRef.current = true;
    isDraggingRef.current  = false;
    const ds = day.format("YYYY-MM-DD");
    setDragAnchor(ds);
    setDragCurrent(ds);
  }, []);

  const handleDragEnter = useCallback((day) => {
    if (!mouseIsDownRef.current) return;
    const ds = day.format("YYYY-MM-DD");
    setDragCurrent((prev) => { if (prev !== ds) isDraggingRef.current = true; return ds; });
  }, []);

  useEffect(() => {
    const handleMouseUp = () => {
      if (mouseIsDownRef.current && isDraggingRef.current && dragAnchor && dragCurrent) {
        const [start, end] = normalizeRange(dragAnchor, dragCurrent);
        setSelectedEntry(null);
        setSelectedDate(start);
        setSelectedEndDate(end);
        setDialogOpen(true);
      }
      mouseIsDownRef.current = false;
      isDraggingRef.current  = false;
      setDragAnchor(null);
      setDragCurrent(null);
    };
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragAnchor, dragCurrent]);

  const isDaySelected = useCallback((ds) => {
    if (!dragAnchor || !dragCurrent || !isDraggingRef.current) return false;
    const [start, end] = normalizeRange(dragAnchor, dragCurrent);
    return ds >= start && ds <= end;
  }, [dragAnchor, dragCurrent]);

  const handleDayClick = (date) => {
    if (isDraggingRef.current) return;
    setSelectedEntry(null);
    setSelectedDate(date.format("YYYY-MM-DD"));
    setSelectedEndDate(null);
    setDialogOpen(true);
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setSelectedDate(null);
    setSelectedEndDate(null);
    setDialogOpen(true);
  };

  const handleSave = async (id, form) => {
    const payload = {
      employee: form.employee, type: form.type, status: form.status,
      start_date: form.start_date, end_date: form.end_date,
      half_day: form.half_day || false, notes: form.notes || "",
    };
    if (id) {
      await axiosInstance.patch(`/team-calendar/${id}/`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
    } else {
      await axiosInstance.post("/team-calendar/", payload, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
    }
    fetchData();
  };

  const handleDelete = async (id) => {
    await axiosInstance.delete(`/team-calendar/${id}/`, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
    fetchData();
  };

  const handleApprove = async (id) => {
    await axiosInstance.post(`/team-calendar/${id}/approve/`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
    fetchData();
  };

  const handleReject = async (id) => {
    await axiosInstance.post(`/team-calendar/${id}/reject/`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
    fetchData();
  };

  const navigatePrev = () => setCurrentDate((d) =>
    viewMode === "month" ? d.subtract(1, "month") :
    (viewMode === "week" || viewMode === "workweek") ? d.subtract(1, "week") : d.subtract(1, "year"));
  const navigateNext = () => setCurrentDate((d) =>
    viewMode === "month" ? d.add(1, "month") :
    (viewMode === "week" || viewMode === "workweek") ? d.add(1, "week") : d.add(1, "year"));

  const headerLabel =
    viewMode === "month" ? currentDate.locale("de").format("MMMM YYYY") :
    (viewMode === "week" || viewMode === "workweek") ? `KW ${currentDate.isoWeek()} · ${currentDate.startOf("isoWeek").format("DD.MM.")}–${currentDate.endOf("isoWeek").format("DD.MM.YYYY")}` :
    currentDate.format("YYYY");

  const allSelected = filterMembers.length === members.length;
  const toggleMember = (id) => {
    setFilterMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      {/* ── A · Kopf: Titel + der eine gefüllte Button ── */}
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
        <Typography variant="h4" component="h1">Teamkalender</Typography>
        <Button variant="contained" size="large" startIcon={<AddIcon />}
          onClick={() => { setSelectedEntry(null); setSelectedDate(dayjs().format("YYYY-MM-DD")); setSelectedEndDate(null); setDialogOpen(true); }}
          sx={{ alignSelf: { xs: "flex-start", md: "auto" } }}>
          Neuer Eintrag
        </Button>
      </Stack>

      {/* ── B · Eigene Urlaubsbilanz ── */}
      <BalanceCard balance={vacationBalances[currentUserId]} />

      {/* ── C · Nur Manager: offene Anträge + Team-Konten ── */}
      {isManager && (
        <ApprovalInbox entries={entries} members={members} holidays={holidays}
          onApprove={handleApprove} onReject={handleReject} onEntryClick={handleEntryClick} />
      )}
      {isManager && (
        <TeamAccountsPanel members={members} balances={vacationBalances} onUpdateAllowance={handleUpdateAllowance} />
      )}

      {/* ── D · Kalender als Fläche ── */}
      <SectionCard>

        {/* Toolbar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>

          {/* Navigation */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton size="small" onClick={navigatePrev}><ChevronLeftIcon /></IconButton>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: "center", fontWeight: 600 }}>{headerLabel}</Typography>
            <IconButton size="small" onClick={navigateNext}><ChevronRightIcon /></IconButton>
            <Tooltip title="Heute">
              <Button size="small" startIcon={<TodayIcon />} onClick={() => setCurrentDate(dayjs())} color="inherit"
                sx={{ ml: 0.5, minWidth: "auto", color: "text.secondary" }}>
                Heute
              </Button>
            </Tooltip>
          </Box>

          {/* Ansichten als stille Pillen */}
          <Box sx={{ display: "flex", gap: 0.25, flexWrap: "wrap" }}>
            {[{ key: "month", label: "Monat" }, { key: "week", label: "Woche" }, { key: "workweek", label: "Arbeitswoche" }, { key: "year", label: "Jahr" }].map((v) => {
              const active = viewMode === v.key;
              return (
                <Box key={v.key} role="button" tabIndex={0}
                  onClick={() => setViewMode(v.key)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewMode(v.key); } }}
                  sx={{
                    fontSize: "0.8rem", fontWeight: 600, px: active ? 1.25 : 0.75, py: 0.3, borderRadius: 999,
                    cursor: "pointer", userSelect: "none",
                    color: active ? "primary.main" : "text.disabled",
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                    "&:hover": { color: active ? "primary.main" : "text.secondary" },
                    "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
                  }}>
                  {v.label}
                </Box>
              );
            })}
          </Box>

          {/* Mitarbeiter-Filter als Avatar-Chips */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Box role="button" tabIndex={0}
              onClick={() => setFilterMembers(members.map((m) => m.id))}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFilterMembers(members.map((m) => m.id)); } }}
              sx={{
                fontSize: "0.75rem", fontWeight: 600, px: 1, py: 0.3, borderRadius: 999, cursor: "pointer", userSelect: "none",
                color: allSelected ? "primary.main" : "text.disabled",
                bgcolor: allSelected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                "&:hover": { color: "primary.main" },
                "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 1 },
              }}>
              Alle
            </Box>
            {members.map((m) => {
              const mc = getMemberColor(m._index, theme);
              const name = m.first_name ? `${m.first_name} ${m.last_name}` : m.username;
              const active = filterMembers.includes(m.id);
              return (
                <Tooltip key={m.id} title={name} placement="top">
                  <Avatar
                    onClick={() => toggleMember(m.id)}
                    sx={{
                      width: 28, height: 28, fontSize: "0.68rem", fontWeight: 700, cursor: "pointer",
                      bgcolor: active ? mc.color : "action.disabledBackground",
                      color: active ? "#fff" : "text.disabled",
                      opacity: active ? 1 : 0.6,
                      transition: "all .12s",
                      "&:hover": { opacity: 1 },
                    }}>
                    {initials(name)}
                  </Avatar>
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        {/* Legende */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
          <LegendPopover />
          <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>Legende</Typography>
        </Box>

        {/* ── Kalender-Ansicht ── */}
        {viewMode === "month" && (
          <MonthView currentDate={currentDate} entries={filteredEntries} members={members} holidays={holidays}
            onDayClick={handleDayClick} onEntryClick={handleEntryClick}
            onDragStart={handleDragStart} onDragEnter={handleDragEnter} isDaySelected={isDaySelected} />
        )}
        {viewMode === "week" && (
          <WeekView currentDate={currentDate} entries={filteredEntries} members={members} holidays={holidays}
            onDayClick={handleDayClick} onEntryClick={handleEntryClick}
            onDragStart={handleDragStart} onDragEnter={handleDragEnter} isDaySelected={isDaySelected} />
        )}
        {viewMode === "workweek" && (
          <WorkWeekView currentDate={currentDate} entries={filteredEntries} members={members} holidays={holidays}
            onDayClick={handleDayClick} onEntryClick={handleEntryClick}
            onDragStart={handleDragStart} onDragEnter={handleDragEnter} isDaySelected={isDaySelected} />
        )}
        {viewMode === "year" && (
          <YearView currentDate={currentDate} entries={filteredEntries} members={members} holidays={holidays} onDayClick={handleDayClick} />
        )}
      </SectionCard>

      {/* Dialog */}
      <EntryDialog
        open={dialogOpen}
        entry={selectedEntry}
        prefillDate={selectedDate}
        prefillEndDate={selectedEndDate}
        members={members}
        vacationBalances={vacationBalances}
        currentUserId={currentUserId}
        isManager={isManager}
        holidays={holidays}
        onClose={() => { setDialogOpen(false); setSelectedEntry(null); setSelectedDate(null); setSelectedEndDate(null); }}
        onSave={handleSave}
        onDelete={handleDelete}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </Box>
  );
};

export default TeamCalendar;